import { useLegendState as useState } from "@/hooks/useLegendState";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { PinVerificationDialog } from "@/components/PinVerificationDialog";
import { queryClient } from "@/lib/queryClient";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import { topicsService } from "@/services/topicsService";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { chatThreadsService } from "@/services/chatThreadsService";
import { pinService, type PinStatus } from "@/services/pinService";
import { useToast } from "@/hooks/use-toast";

type EntityType = "space" | "topic" | "document" | "thread";

interface EntityLockButtonProps {
  entityType: EntityType;
  entityId: string;
  isLocked: boolean;
  onLockChange?: (locked: boolean) => void;
  size?: "sm" | "default" | "menu";
  customTitle?: string;
  /** For entityType "space": when set, lock is scoped to this workspace; when null/undefined, personal. */
  workspaceId?: string | null;
}

function LockIcon({ isLocked, className }: { isLocked: boolean; className?: string }) {
  if (isLocked) {
    return (
      <div className={`relative inline-flex items-center justify-center ${className}`}>
        <div className="absolute inset-0 bg-red-500/20 rounded-full" />
        <svg 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          className="h-full w-full p-0.5 text-red-600"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
    );
  }
  
  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      <div className="absolute inset-0 bg-green-500/20 rounded-full" />
      <svg 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        className="h-full w-full p-0.5 text-green-600"
      >
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 9.9-1" />
        <path d="M17 6V4" />
      </svg>
    </div>
  );
}

export function EntityLockButton({
  entityType,
  entityId,
  isLocked,
  onLockChange,
  size = "sm",
  customTitle,
  workspaceId,
}: EntityLockButtonProps) {
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<"lock" | "unlock" | null>(null);
  const { toast } = useToast();

  const {
    data: pinStatus,
    isPending,
    isError,
    error: pinStatusError,
  } = useQuery<PinStatus>({
    queryKey: ["pin-status"],
    queryFn: () => pinService.getStatus(),
  });

  const toggleLockMutation = useMutation({
    mutationFn: async ({ id, lock }: { id: string; lock: boolean }) => {
      switch (entityType) {
        case "space":
          return await mastermindSpacesService.lockSpace(id, lock, workspaceId ?? null);
        case "topic":
          return await topicsService.lockTopic(id, lock);
        case "document":
          return await knowledgeBaseService.lockDocument(id, lock);
        case "thread":
          return await chatThreadsService.lockChatThread(id, lock, workspaceId ?? null);
        default:
          throw new Error(`Unknown entity type: ${entityType}`);
      }
    },
    onSuccess: (_, variables) => {
      switch (entityType) {
        case "space":
          queryClient.invalidateQueries({ queryKey: ["spaces"] });
          break;
        case "topic":
          queryClient.invalidateQueries({ queryKey: ["spaces"] });
          queryClient.invalidateQueries({ queryKey: ["topics"] });
          // Space page fetches topics with ["/api/spaces", spaceId, "topics"] — invalidate so lock icon updates
          queryClient.invalidateQueries({ queryKey: ["/api/spaces"] });
          queryClient.invalidateQueries({ queryKey: ["/api/topics"] });
          break;
        case "document":
          queryClient.invalidateQueries({ queryKey: ["knowledgebase"] });
          break;
        case "thread":
          queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
          queryClient.invalidateQueries({ queryKey: ["/api/chat-thread-summaries"] });
          break;
      }
      onLockChange?.(variables.lock);
    },
  });

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isPending) {
      return;
    }

    if (isError) {
      toast({
        title: "Could not verify PIN status",
        description:
          pinStatusError instanceof Error
            ? pinStatusError.message
            : "Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (pinStatus == null) {
      return;
    }

    if (!pinStatus.hasPinSet) {
      window.location.href = "/settings?tab=security";
      return;
    }

    const action = isLocked ? "unlock" : "lock";
    setPendingAction(action);

    if (!pinStatus.isSessionValid) {
      setShowPinDialog(true);
      return;
    }

    toggleLockMutation.mutate({ id: entityId, lock: !isLocked });
  };

  const handlePinSuccess = () => {
    if (pendingAction) {
      toggleLockMutation.mutate({ id: entityId, lock: pendingAction === "lock" });
    }
    setPendingAction(null);
  };

  const entityLabels: Record<EntityType, string> = {
    space: "Space",
    topic: "Topic",
    document: "Document",
    thread: "Thread",
  };

  const iconSize = size === "sm" ? "h-4 w-4" : size === "menu" ? "h-4 w-4" : "h-5 w-5";
  const buttonSize = size === "sm" ? "h-7 w-7" : "h-8 w-8";
  
  const defaultTitle = isLocked ? `Unlock ${entityLabels[entityType].toLowerCase()}` : `Lock ${entityLabels[entityType].toLowerCase()}`;
  const displayTitle = customTitle || defaultTitle;

  // Menu variant - display as a menu item with text
  if (size === "menu") {
    return (
      <>
        <div
          className="flex items-center w-full cursor-pointer"
          onClick={handleClick}
          data-testid={`button-lock-${entityType}-${entityId}`}
        >
          {toggleLockMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <LockIcon isLocked={isLocked} className="h-4 w-4 mr-2" />
          )}
          <span>{customTitle || (isLocked ? `Unlock ${entityLabels[entityType]}` : `Lock ${entityLabels[entityType]}`)}</span>
        </div>

        <PinVerificationDialog
          open={showPinDialog}
          onOpenChange={setShowPinDialog}
          onSuccess={handlePinSuccess}
          title={pendingAction === "lock" ? `Lock ${entityLabels[entityType]}` : `Unlock ${entityLabels[entityType]}`}
          description={
            pendingAction === "lock"
              ? `Enter your PIN to lock this ${entityLabels[entityType].toLowerCase()}`
              : `Enter your PIN to unlock this ${entityLabels[entityType].toLowerCase()}`
          }
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className={buttonSize}
        onClick={handleClick}
        disabled={toggleLockMutation.isPending}
        title={displayTitle}
        data-testid={`button-lock-${entityType}-${entityId}`}
      >
        {toggleLockMutation.isPending ? (
          <Loader2 className={`${iconSize} animate-spin`} />
        ) : (
          <LockIcon isLocked={isLocked} className={iconSize} />
        )}
      </Button>

      <PinVerificationDialog
        open={showPinDialog}
        onOpenChange={setShowPinDialog}
        onSuccess={handlePinSuccess}
        title={pendingAction === "lock" ? `Lock ${entityLabels[entityType]}` : `Unlock ${entityLabels[entityType]}`}
        description={
          pendingAction === "lock"
            ? `Enter your PIN to lock this ${entityLabels[entityType].toLowerCase()}`
            : `Enter your PIN to unlock this ${entityLabels[entityType].toLowerCase()}`
        }
      />
    </>
  );
}

interface LockedEntityOverlayProps {
  entityType: EntityType;
  onUnlock: () => void;
  /** When true, the parent renders the PIN dialog; overlay only shows the button that calls onUnlock. Use to avoid double PIN prompt. */
  dialogControlledByParent?: boolean;
}

export function LockedEntityOverlay({
  entityType,
  onUnlock,
  dialogControlledByParent = false,
}: LockedEntityOverlayProps) {
  const [showPinDialog, setShowPinDialog] = useState(false);

  const entityLabels: Record<EntityType, string> = {
    space: "Space",
    topic: "Topic",
    document: "Document",
    thread: "Thread",
  };

  const handleUnlockClick = () => {
    if (dialogControlledByParent) {
      onUnlock();
    } else {
      setShowPinDialog(true);
    }
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="bg-red-500/10 p-4 rounded-full mb-4">
          <LockIcon isLocked={true} className="h-12 w-12" />
        </div>
        <h3 className="text-lg font-semibold mb-2">{entityLabels[entityType]} is Locked</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-md">
          This {entityLabels[entityType].toLowerCase()} is protected with a PIN. Enter your PIN to view its contents.
        </p>
        <Button onClick={handleUnlockClick} data-testid={`button-unlock-${entityType}`}>
          <LockIcon isLocked={true} className="h-4 w-4 mr-2" />
          Unlock {entityLabels[entityType]}
        </Button>
      </div>

      {!dialogControlledByParent && (
        <PinVerificationDialog
          open={showPinDialog}
          onOpenChange={setShowPinDialog}
          onSuccess={onUnlock}
          title={`Unlock ${entityLabels[entityType]}`}
          description={`Enter your PIN to view this locked ${entityLabels[entityType].toLowerCase()}`}
        />
      )}
    </>
  );
}
